"use client";

import { useState, type ReactNode } from "react";
import {
  SymAblauf,
  SymBild,
  SymInfo,
  SymKalender,
  SymSeite,
  SymTipp,
  SymVerboten,
  SymWarnung,
  SymWichtig,
} from "./lese-symbole";
import { VerweisChip } from "./lese-verweis-chip";
import {
  VERWEIS_MODULWORT,
  VERWEIS_NICHT_VERFUEGBAR_HREF,
  VERWEIS_NICHT_VERFUEGBAR_MARKE,
  VERWEIS_NICHT_VERFUEGBAR_TEXT,
  istVerweisPlatzhalter,
} from "./lese-verweis-art";

/*
 * Darstellungen des Lesepfads der Editor-Einfassung (Paket E-W1, G-W3 Variante A): Tabelle, Hinweisfeld, Bild mit
 * Unterschrift, Datum, Unterseiten-Verzeichnis, Verweis, Personen-, CI- und Workflow-Chip und die Karte „Nur Ansicht“ für
 * das Workflow-Diagramm. Reine Darstellung: Texte (Titel, Namen, Unterschriften) stehen immer als Text im Markup, nie
 * als HTML; Adressen kommen geprüft vom Editor (http(s) und relativ). Die Bausteine kennen TipTap nicht; der Editor reicht
 * ihnen Daten (`HornLeseAnsichten` in `@horn/editor/kern`, strukturell gleich, belegt der Typecheck des Verbrauchers und
 * der Test `editor-lesen.test.tsx`). Eigenbau aus Standardklassen nach dem Zusammenbau `w3-a-lesen`: Abweichung AW-051.
 */

/** Anker für die Hovercard (Delegation am Container im Editor). */
export interface LeseAnker {
  "data-hover-art": "person" | "wiki" | "ci" | "workflow";
  "data-hover-id": string;
  tabIndex: 0;
}

export interface LeseUnterseite {
  id: string;
  title: string;
  href: string | null;
  children: LeseUnterseite[];
}

// ---------------------------------------------------------------------------------------------
// Tabelle

export function LeseTabelle({ children }: { children: ReactNode }) {
  return (
    <>
      {/* Preline: component/tables/tables-rounded-tables */}
      <div className="overflow-x-auto bg-layer border border-layer-line rounded-xl">
        <table className="min-w-full">{children}</table>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Hinweisfeld (vier Töne; die fünf Alert-Töne bleiben dem Seitenbaustein)

export type LeseHinweisTon = "info" | "ok" | "warn" | "risk";

export const HINWEIS_KLASSEN: Record<LeseHinweisTon, string> = {
  info: "bg-primary-100 border border-primary-200 text-sm text-primary-800 rounded-lg p-4 flex gap-x-3",
  ok: "bg-teal-100 border border-teal-200 text-sm text-teal-800 rounded-lg p-4 flex gap-x-3",
  warn: "bg-yellow-100 border border-yellow-200 text-sm text-yellow-800 rounded-lg p-4 flex gap-x-3",
  risk: "bg-red-100 border border-red-200 text-sm text-red-800 rounded-lg p-4 flex gap-x-3",
};
export const HINWEIS_NAME: Record<LeseHinweisTon, string> = { info: "Hinweis", ok: "Tipp", warn: "Warnung", risk: "Wichtig" };

export function HinweisSymbol({ ton }: { ton: LeseHinweisTon }) {
  if (ton === "ok") return <SymTipp />;
  if (ton === "warn") return <SymWarnung />;
  if (ton === "risk") return <SymWichtig />;
  return <SymInfo />;
}

export function LeseHinweisfeld({ ton, titel, children }: { ton: LeseHinweisTon; titel: string; children: ReactNode }) {
  const t = ton in HINWEIS_KLASSEN ? ton : "info";
  return (
    <>
      {/* Preline: component/alerts/alerts-soft-color-variants */}
      <div className={HINWEIS_KLASSEN[t]} role="note">
        <span className="mt-0.5">
          <HinweisSymbol ton={t} />
        </span>
        <div className="min-w-0 flex-1">
          {titel ? <div className="font-semibold">{titel}</div> : <span className="sr-only">{HINWEIS_NAME[t]}: </span>}
          {children}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Bild mit Unterschrift

export const BILD_FEHLT_SATZ = "Dieses Bild steht nicht zur Verfügung.";

export function LeseBild({ src, alt, unterschrift }: { src: string | null; alt: string; unterschrift: string }) {
  // Ein Bild, das nicht lädt (unzulässige Adresse, 404 auch bei fehlendem Recht, r95), zeigt immer denselben Satz ohne Grund.
  const [kaputt, setKaputt] = useState(false);
  const zeigeBild = src !== null && !kaputt;
  return (
    <>
      {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-file-views/profile-files-list */}
      <figure className="space-y-2">
        {zeigeBild ? (
          <img
            src={src}
            alt={alt}
            loading="lazy"
            className="mx-auto h-auto max-w-full rounded-xl border border-layer-line"
            onError={() => setKaputt(true)}
          />
        ) : (
          <div className="flex flex-col items-center justify-center gap-y-2 rounded-xl border border-layer-line bg-muted p-8 text-muted-foreground-1">
            <SymBild />
            <span className="text-sm">{BILD_FEHLT_SATZ}</span>
          </div>
        )}
        {unterschrift ? <figcaption className="text-center text-xs text-muted-foreground-1">{unterschrift}</figcaption> : null}
      </figure>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Datum

export function LeseDatum({ iso, zeit, text }: { iso: string; zeit: string | null; text: string }) {
  return (
    <>
      {/* Preline: component/badge/badge-soft-color-variants */}
      <time
        dateTime={zeit ? iso + "T" + zeit : iso}
        className="inline-flex items-center gap-x-1 rounded-md border border-layer-line bg-layer px-1.5 py-0.5 text-sm text-foreground"
      >
        <SymKalender />
        {text}
      </time>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Unterseiten-Verzeichnis

/** Ab dieser Ebene sind Unterseiten eingeklappt (wie die Altansicht: Ebenen 1 bis 3 offen). */
const OFFENE_EBENEN = 3;

export function UnterseitenZeilen({ eintraege, ebene }: { eintraege: LeseUnterseite[]; ebene: number }) {
  return (
    <ul className="divide-y divide-layer-line">
      {eintraege.map((e) => (
        <li key={e.id}>
          <div className="flex items-center gap-x-2 px-4 py-2.5 text-sm">
            <SymSeite />
            {e.href ? (
              <a
                href={e.href}
                className="min-w-0 flex-1 truncate font-medium text-primary hover:text-primary-hover hover:underline focus:outline-hidden focus-visible:underline"
              >
                {e.title}
              </a>
            ) : (
              <span className="min-w-0 flex-1 truncate text-foreground">{e.title}</span>
            )}
          </div>
          {e.children.length > 0 ? (
            ebene < OFFENE_EBENEN ? (
              <div className="ps-6 border-t border-layer-line">
                <UnterseitenZeilen eintraege={e.children} ebene={ebene + 1} />
              </div>
            ) : (
              <details className="ps-6 border-t border-layer-line">
                <summary className="cursor-pointer px-4 py-2 text-xs text-muted-foreground-1 hover:text-foreground focus:outline-hidden focus-visible:underline">
                  Weitere Ebenen ({e.children.length})
                </summary>
                <UnterseitenZeilen eintraege={e.children} ebene={ebene + 1} />
              </details>
            )
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function LeseUnterseiten({ ueberschrift, eintraege }: { ueberschrift: string; eintraege: LeseUnterseite[] | null }) {
  // Ein leeres Verzeichnis zeigt der Lesepfad nicht; der Editor blendet den Knoten dann aus.
  if (eintraege !== null && eintraege.length === 0) return null;
  return (
    <>
      {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-list-groups/icon-action-list-with-dashed-dividers */}
      <section className="bg-layer border border-layer-line rounded-xl" aria-label={ueberschrift} aria-busy={eintraege === null}>
        <div className="px-4 py-3 border-b border-layer-line text-sm font-semibold text-foreground">{ueberschrift}</div>
        {eintraege === null ? (
          <div className="px-4 py-3 text-sm text-muted-foreground-1">Lädt …</div>
        ) : (
          <UnterseitenZeilen eintraege={eintraege} ebene={1} />
        )}
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Verweis, Chips

export function LeseVerweis({
  href,
  wiki,
  neuesFenster,
  anker,
  children,
}: {
  href: string | null;
  wiki: boolean;
  neuesFenster: boolean;
  anker: LeseAnker | null;
  children: ReactNode;
}) {
  // Eine unzulässige Adresse (z. B. `javascript:`) ergibt keinen Link, der Text bleibt stehen.
  if (href === null) return <span>{children}</span>;
  // Die Server-Bereinigung ersetzt einen Wiki-Verweis, den die Person nicht sehen darf, durch diese Marke: ein Chip ohne
  // Adresse und ohne Namen (der Text der Marke ist schon der feste Text).
  if (href === VERWEIS_NICHT_VERFUEGBAR_HREF) {
    return <VerweisChip art="wiki" zustand="nicht-verfuegbar">{children}</VerweisChip>;
  }
  // Wiki-Verweis: Chip Variante A (Wiki | Seitentitel); jeder andere Verweis bleibt ein unterstrichener Link.
  if (wiki) {
    return (
      <VerweisChip art="wiki" href={href} neuesFenster={neuesFenster} anker={anker}>
        {children}
      </VerweisChip>
    );
  }
  return (
    <>
      {/* Preline: component/badge/badge-soft-color-variants */}
      <a
        href={href}
        target={neuesFenster ? "_blank" : undefined}
        rel={neuesFenster ? "noopener noreferrer" : undefined}
        data-hover-art={anker?.["data-hover-art"]}
        data-hover-id={anker?.["data-hover-id"]}
        className="text-primary font-medium underline underline-offset-2 hover:text-primary-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus rounded-xs"
      >
        {children}
      </a>
    </>
  );
}

/*
 * Person, CI, Workflow (Variante A, `VerweisChip`). Ohne Anker (die Server-Bereinigung hat die Kennung entfernt, weil die
 * Person das Ziel nicht sehen darf) steht „Nicht verfügbar“ statt des Namens; der Name im Seiteninhalt ist dann schon leer.
 * Eine ausgeschiedene Person behält den Namen, der Chip ist leise.
 */
/** Kein Anker, oder der Platzhalter der Bearbeiten-Ansicht (Kennung mit `nv~`): beide heißen „nicht verfügbar“, ohne Hover. */
function ohneZiel(anker: LeseAnker | null): boolean {
  return anker === null || istVerweisPlatzhalter(anker["data-hover-id"]);
}

export function LesePerson({ name, inaktiv, anker }: { name: string; inaktiv: boolean; anker: LeseAnker | null }) {
  if (ohneZiel(anker)) return <VerweisChip art="person" zustand="nicht-verfuegbar" />;
  return (
    <VerweisChip art="person" zustand={inaktiv ? "ausgeschieden" : "normal"} anker={anker}>
      {name}
    </VerweisChip>
  );
}

/** CI-Erwähnung: der Name steht im Seiteninhalt; ein Link in die CMDB gehört nur in die Hovercard und nur mit `cmdb:read`. */
export function LeseCi({
  name,
  anker,
  sicht = null,
}: {
  name: string;
  anker: LeseAnker | null;
  /** Vom Server gesetzt (Grunddaten-Sicht, W182): `grundsicht` = Name und Typ ohne Vollrecht, `gattung` = nur das Wort der Art. */
  sicht?: "grundsicht" | "gattung" | null;
}) {
  // Gattung: nur das Wort, keine Kennung im Client; der Chip setzt selbst die feste Marke (Karte ohne Serveraufruf).
  if (sicht === "gattung") return <VerweisChip art="cmdb" zustand="gattung">{name}</VerweisChip>;
  // Grundsicht: Name (aktueller Stand aus dem Port), kein Link; mit Kennung öffnet die Karte Name und Typ ohne Link.
  if (sicht === "grundsicht") {
    return (
      <VerweisChip art="cmdb" zustand="grundsicht" anker={ohneZiel(anker) ? null : anker}>
        {name}
      </VerweisChip>
    );
  }
  if (ohneZiel(anker)) return <VerweisChip art="cmdb" zustand="nicht-verfuegbar" />;
  return (
    <VerweisChip art="cmdb" anker={anker}>
      {name}
    </VerweisChip>
  );
}

export function LeseWorkflowChip({ name, anker }: { name: string; anker: LeseAnker | null }) {
  if (ohneZiel(anker)) return <VerweisChip art="workflow" zustand="nicht-verfuegbar" />;
  return (
    <VerweisChip art="workflow" anker={anker}>
      {name}
    </VerweisChip>
  );
}

// ---------------------------------------------------------------------------------------------
// Workflow-Diagramm: Karte „Nur Ansicht“ (F9-a, kein Graph)

const KARTE_NORMAL = "bg-layer border border-layer-line rounded-xl p-4";
const KARTE_NICHT_VERFUEGBAR = "bg-surface border border-dashed border-line-2 rounded-xl p-4";

export function LeseWorkflowKarte({ name }: { name: string }) {
  // Die Server-Bereinigung setzt die Marke an die Stelle des Schlüssels, wenn die Person die Definition nicht sehen darf.
  if (name === VERWEIS_NICHT_VERFUEGBAR_MARKE) {
    return (
      <>
        {/* Preline: ui-components/ui-components-sub-actions-status-progress/ui-components-cat-badge-styles/badge-with-divider-segment */}
        <div className={KARTE_NICHT_VERFUEGBAR}>
          <div className="flex items-center gap-x-2 text-sm text-muted-foreground-1">
            <span className="inline-flex shrink-0 items-center gap-1.5 text-xs">
              <SymVerboten klein />
              {VERWEIS_MODULWORT.workflow}
            </span>
            <span className="border-s border-dashed border-line-2 ps-2">{VERWEIS_NICHT_VERFUEGBAR_TEXT}</span>
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-actions-status-progress/ui-components-cat-badge-styles/badge-with-divider-segment */}
      <div className={KARTE_NORMAL}>
        <div className="flex items-center justify-between gap-x-2">
          <div className="flex min-w-0 items-center gap-x-2 text-sm text-foreground">
            <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground-1">
              <SymAblauf klein />
              {VERWEIS_MODULWORT.workflow}
            </span>
            {name ? <span className="min-w-0 truncate border-s border-layer-line ps-2 font-medium">{name}</span> : null}
          </div>
          <span className="shrink-0 text-xs text-muted-foreground-1">Nur Ansicht</span>
        </div>
      </div>
    </>
  );
}

/** Alle Darstellungen des Lesepfads in der Form, die `useHornEditor({ lesen: { ansichten } })` erwartet. */
export const LESE_ANSICHTEN = {
  tabelle: LeseTabelle,
  hinweisfeld: LeseHinweisfeld,
  bild: LeseBild,
  datum: LeseDatum,
  unterseiten: LeseUnterseiten,
  verweis: LeseVerweis,
  person: LesePerson,
  ci: LeseCi,
  workflowChip: LeseWorkflowChip,
  workflowKarte: LeseWorkflowKarte,
};
